import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';

// Card variants for different use cases
const cardVariants = cva(
  [
    // Base card styles - no color defaults, let variants handle it
    'border rounded-lg shadow-md',
    'transition-all duration-200 ease-out-cubic',
  ],
  {
    variants: {
      variant: {
        default: 'bg-slate-800 border-slate-700',
        light: 'bg-white border-gray-200 shadow-sm',  // Light variant for workflow/dashboard
        agent: [
          'bg-slate-800 border-slate-700 relative',
          'before:absolute before:top-3 before:right-3 before:w-2 before:h-2 before:rounded-full',
          'before:bg-slate-500', // Default idle state
        ],
        workflow: [
          'bg-slate-700 border-2 border-blue-500 rounded-xl',
          'shadow-lg',
        ],
        decision: [
          'bg-gradient-to-br from-slate-800 to-slate-700',
          'border border-amber-500 relative',
          'before:content-["⚠️"] before:absolute before:top-3 before:right-3 before:text-lg',
        ],
      },
      size: {
        small: 'p-3',
        medium: 'p-4',
        large: 'p-6',
      },
      interactive: {
        true: [
          'cursor-pointer',
          'hover:-translate-y-0.5 hover:shadow-lg hover:border-slate-600',
          'active:-translate-y-0.5 active:shadow-md',
        ],
        false: '',
      },
      agentStatus: {
        idle: 'before:bg-slate-500',
        active: [
          'before:bg-emerald-400 before:shadow-emerald-400/30 before:shadow-md',
          'before:animate-pulse',
        ],
        thinking: [
          'before:bg-blue-400 before:shadow-blue-400/30 before:shadow-md',
          'before:animate-pulse',
        ],
        waiting: [
          'before:bg-amber-400 before:shadow-amber-400/30 before:shadow-md',
          'before:animate-bounce',
        ],
        error: [
          'before:bg-red-400 before:shadow-red-400/30 before:shadow-md',
          'before:animate-pulse',
        ],
        complete: 'before:bg-emerald-500',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'medium',
      interactive: false,
      agentStatus: 'idle',
    },
  }
);

// Base Card component props
export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  asChild?: boolean;
}

// Base Card Component
export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, size, interactive, agentStatus, asChild = false, ...props }, ref) => {
    const Comp = asChild ? 'div' : 'div';

    return (
      <Comp
        ref={ref}
        className={cn(cardVariants({ variant, size, interactive, agentStatus, className }))}
        {...props}
      />
    );
  }
);

Card.displayName = 'Card';

// Card Header Component
export const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 pb-4', className)}
    {...props}
  />
));

CardHeader.displayName = 'CardHeader';

// Card Title Component
export const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));

CardTitle.displayName = 'CardTitle';

// Card Description Component
export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm', className)}
    {...props}
  />
));

CardDescription.displayName = 'CardDescription';

// Card Content Component
export const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('', className)} {...props} />
));

CardContent.displayName = 'CardContent';

// Card Footer Component
export const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center pt-4 border-t', className)}
    {...props}
  />
));

CardFooter.displayName = 'CardFooter';

// Agent Status Card - Specialized for agent monitoring
export interface AgentStatusCardProps extends Omit<CardProps, 'variant'> {
  agent: {
    id: string;
    name: string;
    type: string;
    status: 'active' | 'thinking' | 'waiting' | 'error' | 'complete' | 'idle';
    currentTask?: string;
    progress?: number;
    lastUpdate: Date;
    icon?: string;
  };
  onInteract?: (action: string, agentId: string) => void;
  showDetails?: boolean;
  compact?: boolean;
}

export const AgentStatusCard = React.forwardRef<HTMLDivElement, AgentStatusCardProps>(
  ({ agent, onInteract, showDetails = false, compact = false, className, ...props }, ref) => {
    const getStatusColor = (status: string) => {
      const colors = {
        active: 'text-emerald-400',
        thinking: 'text-blue-400',
        waiting: 'text-amber-400',
        error: 'text-red-400',
        complete: 'text-emerald-500',
        idle: 'text-slate-500',
      };
      return colors[status as keyof typeof colors] || colors.idle;
    };

    const getStatusIcon = (status: string) => {
      const icons = {
        active: '⚡',
        thinking: '💭',
        waiting: '⏳',
        error: '⚠️',
        complete: '✅',
        idle: '💤',
      };
      return icons[status as keyof typeof icons] || icons.idle;
    };

    const formatLastUpdate = (date: Date) => {
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    };

    return (
      <Card
        ref={ref}
        variant="agent"
        size={compact ? 'small' : 'medium'}
        interactive={!!onInteract}
        agentStatus={agent.status}
        className={cn('group', className)}
        onClick={() => onInteract?.('view', agent.id)}
        {...props}
      >
        <CardHeader className={compact ? 'pb-2' : 'pb-4'}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              {agent.icon && (
                <span className="text-lg" role="img" aria-label={`${agent.type} agent`}>
                  {agent.icon}
                </span>
              )}
              <div>
                <CardTitle className={compact ? 'text-base' : 'text-lg'}>
                  {agent.name}
                </CardTitle>
                <CardDescription className="flex items-center gap-1">
                  <span className={getStatusColor(agent.status)}>
                    {getStatusIcon(agent.status)}
                  </span>
                  <span className="capitalize">{agent.status}</span>
                  {agent.type && (
                    <>
                      <span className="text-slate-600">•</span>
                      <span>{agent.type}</span>
                    </>
                  )}
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Current Task */}
          {agent.currentTask && (
            <div className="mb-3">
              <p className="text-sm text-slate-300 mb-1">Current Task:</p>
              <p className="text-sm text-slate-400 line-clamp-2">{agent.currentTask}</p>
            </div>
          )}

          {/* Progress Bar */}
          {agent.progress !== undefined && agent.status === 'active' && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-400">Progress</span>
                <span className="text-xs text-slate-300">{Math.round(agent.progress)}%</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-1.5">
                <div
                  className="bg-emerald-400 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${agent.progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Extended Details */}
          {showDetails && !compact && (
            <div className="space-y-2 pt-2 border-t border-slate-700">
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Agent ID:</span>
                <span className="text-slate-300 font-mono">{agent.id}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Last Update:</span>
                <span className="text-slate-300">{formatLastUpdate(agent.lastUpdate)}</span>
              </div>
            </div>
          )}
        </CardContent>

        {/* Quick Actions Footer */}
        {!compact && (
          <CardFooter className="pt-3">
            <div className="flex gap-2 w-full">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onInteract?.('configure', agent.id);
                }}
                className="flex-1 px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 rounded transition-colors"
              >
                Configure
              </button>
              {agent.status === 'idle' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onInteract?.('start', agent.id);
                  }}
                  className="flex-1 px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 rounded transition-colors"
                >
                  Start
                </button>
              )}
              {agent.status === 'active' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onInteract?.('pause', agent.id);
                  }}
                  className="flex-1 px-2 py-1 text-xs bg-amber-600 hover:bg-amber-700 rounded transition-colors"
                >
                  Pause
                </button>
              )}
              {agent.status === 'error' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onInteract?.('restart', agent.id);
                  }}
                  className="flex-1 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                >
                  Restart
                </button>
              )}
            </div>
          </CardFooter>
        )}
      </Card>
    );
  }
);

AgentStatusCard.displayName = 'AgentStatusCard';

// Workflow Card - For workflow orchestration
export interface WorkflowCardProps extends Omit<CardProps, 'variant'> {
  workflow: {
    id: string;
    name: string;
    description?: string;
    status: 'IN-PROGRESS' | 'QUEUED' | 'BLOCKED' | 'SUCCESS' | 'DRAFT';
    agents: Array<{ id: string; name: string; status: string }>;
    progress?: number;
    estimatedCompletion?: Date;
  };
  onWorkflowAction?: (action: string, workflowId: string) => void;
}

export const WorkflowCard = React.forwardRef<HTMLDivElement, WorkflowCardProps>(
  ({ workflow, onWorkflowAction, className, ...props }, ref) => {
    const getStatusColor = (status: string) => {
      const colors = {
        'IN-PROGRESS': 'border-purple-500 bg-purple-500/10',
        QUEUED: 'border-slate-500 bg-slate-500/10',
        BLOCKED: 'border-red-500 bg-red-500/10',
        SUCCESS: 'border-emerald-500 bg-emerald-500/10',
        DRAFT: 'border-amber-500 bg-amber-500/10',
      };
      return colors[status as keyof typeof colors] || colors.DRAFT;
    };

    const activeAgents = workflow.agents.filter(agent => agent.status === 'active').length;

    return (
      <Card
        ref={ref}
        variant="workflow"
        size="medium"
        interactive={!!onWorkflowAction}
        className={cn(getStatusColor(workflow.status), className)}
        onClick={() => onWorkflowAction?.('view', workflow.id)}
        {...props}
      >
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>{workflow.name}</CardTitle>
              <CardDescription className="flex items-center gap-2 mt-1">
                <span className="capitalize">{workflow.status.replace('-', ' ')}</span>
                <span className="text-slate-600">•</span>
                <span>{workflow.agents.length} agents</span>
                {activeAgents > 0 && (
                  <>
                    <span className="text-slate-600">•</span>
                    <span className="text-emerald-400">{activeAgents} active</span>
                  </>
                )}
              </CardDescription>
            </div>
            <div className="text-right text-xs text-slate-400">
              {workflow.estimatedCompletion && (
                <div>
                  ETA: {workflow.estimatedCompletion.toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {workflow.description && (
            <p className="text-sm text-slate-400 mb-3 line-clamp-2">
              {workflow.description}
            </p>
          )}

          {/* Progress for active workflows */}
          {workflow.progress !== undefined && workflow.status === 'IN-PROGRESS' && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-400">Overall Progress</span>
                <span className="text-xs text-slate-300">{Math.round(workflow.progress)}%</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-2">
                <div
                  className="bg-purple-400 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${workflow.progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Agent Summary */}
          <div className="flex flex-wrap gap-1">
            {workflow.agents.slice(0, 5).map((agent) => (
              <span
                key={agent.id}
                className={cn(
                  'inline-flex items-center px-2 py-1 rounded text-xs',
                  agent.status === 'active' && 'bg-emerald-500/20 text-emerald-400',
                  agent.status === 'waiting' && 'bg-amber-500/20 text-amber-400',
                  agent.status === 'idle' && 'bg-slate-500/20 text-slate-400'
                )}
              >
                {agent.name}
              </span>
            ))}
            {workflow.agents.length > 5 && (
              <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-slate-500/20 text-slate-400">
                +{workflow.agents.length - 5} more
              </span>
            )}
          </div>
        </CardContent>

        <CardFooter>
          <div className="flex gap-2 w-full">
            {workflow.status === 'DRAFT' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onWorkflowAction?.('start', workflow.id);
                }}
                className="flex-1 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 rounded transition-colors"
              >
                Start Workflow
              </button>
            )}
            {workflow.status === 'IN-PROGRESS' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onWorkflowAction?.('pause', workflow.id);
                }}
                className="flex-1 px-3 py-1.5 text-sm bg-amber-600 hover:bg-amber-700 rounded transition-colors"
              >
                Pause
              </button>
            )}
            {workflow.status === 'BLOCKED' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onWorkflowAction?.('resolve', workflow.id);
                }}
                className="flex-1 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 rounded transition-colors"
              >
                Resolve
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onWorkflowAction?.('configure', workflow.id);
              }}
              className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 rounded transition-colors"
            >
              Configure
            </button>
          </div>
        </CardFooter>
      </Card>
    );
  }
);

WorkflowCard.displayName = 'WorkflowCard';
